import { useState } from "react";
import { Button, useToast } from "../../../ui/index.js";
import { jerseyMismatches } from "./playerCompareView.js";
import type {
  MappingMatchDto,
  MappingView,
  usePlayerCompareMapping,
} from "./usePlayerCompareMapping.js";

type Mapping = ReturnType<typeof usePlayerCompareMapping>;
type RestError = Error & { body?: { error?: string; message?: string; playerId?: string } };
type Props = {
  mapping: Mapping;
  teamLabel: (teamId: string) => string;
  onMapped: () => void;
};

const SELECT =
  "h-7 min-w-0 flex-1 rounded-md border border-border bg-surface px-1.5 text-[12px] text-ink";

function matchLabel(m: { name: string; date: string; score?: number }): string {
  const head = m.name.startsWith(m.date) ? m.name : `${m.date} ${m.name}`;
  return `${head}${m.score !== undefined ? ` (${m.score.toFixed(2)})` : ""}`;
}

/** dostats mapping: match candidate + auto-mapping + unlinked players. */
export function MappingSection({ mapping, teamLabel, onMapped }: Props) {
  const { toast } = useToast();
  const view = mapping.mapping.data;
  const [matchChoice, setMatchChoice] = useState<string>("");
  const [playerChoice, setPlayerChoice] = useState<Record<string, string>>({});
  // Which action is running — its button shows progress. The request waits
  // for the Hudl lineup (upstream, ~8s), so without this a click looks ignored.
  const [action, setAction] = useState<null | "match" | "unlink" | "automap" | "players">(null);

  if ((mapping.status.loading || mapping.fullPending) && !view) {
    return <p className="text-[12px] text-ink-faint">Xarita yuklanmoqda…</p>;
  }
  if (mapping.statusError && !view) {
    return (
      <p className="text-[12px] text-danger-strong">
        dostats holatini o'qib bo'lmadi: {mapping.statusError.message}
      </p>
    );
  }
  if (!view) {
    const err = mapping.mapping.error as RestError | null;
    return (
      <p className="text-[12px] text-danger-strong">
        Xaritani o'qib bo'lmadi: {err?.body?.message ?? err?.message ?? "noma'lum xato"}
      </p>
    );
  }

  // After a write the view is re-read (the lineup again) — show it, block buttons.
  const refreshing = mapping.mapping.loading;
  const locked = mapping.busy || refreshing || action !== null;
  const run = async (a: NonNullable<typeof action>, f: () => Promise<void>) => {
    setAction(a);
    try {
      await f();
    } finally {
      setAction(null);
    }
  };

  const lineupById = new Map(view.lineup.map((l) => [l.id, l]));
  const unmapped = view.players.filter((p) => p.dostatsId === null);
  const mismatches = jerseyMismatches(view.players, view.lineup);

  // The match select is always visible: current + candidates (dedupe); if there is a
  // current, pre-select it (when matchChoice is empty).
  const currentIdStr = view.match.current ? String(view.match.current.id) : "";
  const matchOptionsById = new Map<number, MappingMatchDto>();
  if (view.match.current) matchOptionsById.set(view.match.current.id, view.match.current);
  for (const c of view.match.candidates) {
    if (!matchOptionsById.has(c.id)) matchOptionsById.set(c.id, c);
  }
  const matchOptions = [...matchOptionsById.values()];
  const selectedMatchId = matchChoice || currentIdStr;

  // With no candidate — we determine the dostats team via a teammate (same local
  // team): via a player who is already linked or has a candidate.
  const remoteTeamFor = (p: MappingView["players"][number]): number | null => {
    const sibling = view.players.find(
      (s) => s.teamId === p.teamId && (s.dostatsId !== null || s.candidates.length > 0),
    );
    if (!sibling) return null;
    const remoteId =
      sibling.dostatsId ?? [...sibling.candidates].sort((a, b) => b.score - a.score)[0]?.remoteId;
    if (remoteId === undefined || remoteId === null) return null;
    return lineupById.get(remoteId)?.teamId ?? null;
  };

  // If the team is determined — that team's lineup, candidates on top (by score).
  // If not — the full lineup (the team is shown in the option name).
  const lineupForPlayer = (p: MappingView["players"][number], remoteTeamId: number | null) => {
    const scoreOf = new Map(p.candidates.map((c) => [c.remoteId, c.score]));
    const pool =
      remoteTeamId !== null ? view.lineup.filter((l) => l.teamId === remoteTeamId) : view.lineup;
    return [...pool].sort((a, b) => (scoreOf.get(b.id) ?? -1) - (scoreOf.get(a.id) ?? -1));
  };

  const runAutomap = () =>
    run("automap", async () => {
      try {
        const r = await mapping.automap();
        if (!r.match) {
          toast({
            tone: "error",
            title: "O'yin aniqlanmadi",
            description: "Nomzodlardan qo'lda tanlang",
          });
          return;
        }
        toast({
          tone: "success",
          title: "Avto xaritalash",
          description:
            `${r.saved ?? 0} o'yinchi bog'landi, ${r.ambiguous?.length ?? 0} ikkilangan, ` +
            `${r.unmapped?.length ?? 0} topilmadi`,
        });
        setPlayerChoice({});
        onMapped();
      } catch (e) {
        const err = e as RestError;
        toast({
          tone: "error",
          title: "Avto xaritalash xatosi",
          description: err.body?.message ?? err.message,
        });
      }
    });

  const saveMatch = (id: number) =>
    run("match", async () => {
      if (!id) return;
      try {
        // The pick stays selected (not reset) — otherwise until the re-read the
        // select would jump back to the old match.
        await mapping.save({ dostatsMatchId: id });
        toast({ tone: "success", title: "O'yin bog'landi" });
        onMapped();
      } catch (e) {
        const err = e as RestError;
        const desc = err.body?.message ?? err.message;
        toast({ tone: "error", title: "Saqlab bo'lmadi", description: desc });
      }
    });

  const unlinkMatch = () =>
    run("unlink", async () => {
      try {
        await mapping.save({ dostatsMatchId: null });
        setMatchChoice("");
        // after unlinking, a recompute only yields the match_unmapped error.
        toast({ tone: "success", title: "O'yin bog'lanishi uzildi" });
      } catch (e) {
        const err = e as RestError;
        toast({
          tone: "error",
          title: "Uzib bo'lmadi",
          description: err.body?.message ?? err.message,
        });
      }
    });

  const playerName = (id: string | undefined): string => {
    if (!id) return id ?? "";
    const p = view.players.find((pl) => pl.id === id);
    return p ? `${p.firstName} ${p.lastName}` : id;
  };

  const savePlayers = () =>
    run("players", async () => {
      const players: Record<string, number | null> = {};
      for (const [pid, rid] of Object.entries(playerChoice)) if (rid) players[pid] = Number(rid);
      if (Object.keys(players).length === 0) return;
      try {
        await mapping.save({ players });
        setPlayerChoice({});
        toast({
          tone: "success",
          title: "O'yinchilar bog'landi",
          description: `${Object.keys(players).length} ta`,
        });
        onMapped();
      } catch (e) {
        const err = e as RestError;
        const desc =
          err.body?.error === "dostats_id_taken"
            ? `Bu dostats id allaqachon ${playerName(err.body.playerId)} ga bog'langan`
            : (err.body?.message ?? err.message);
        toast({ tone: "error", title: "Saqlab bo'lmadi", description: desc });
      }
    });

  return (
    <div className="flex flex-col gap-3">
      {(action !== null || refreshing) && (
        <p className="text-[11px] text-ink-faint">
          ⟳ {refreshing && action === null ? "Xarita yangilanmoqda" : "Bajarilmoqda"} — Hudl tarkibi
          ~10 soniyada keladi…
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        <div className="text-[12px] text-ink-muted">
          dostats o'yini:{" "}
          {view.match.current ? (
            <span className="text-ink">{matchLabel(view.match.current)}</span>
          ) : (
            <span className="text-warning">bog'lanmagan</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            className={SELECT}
            value={selectedMatchId}
            onChange={(e) => setMatchChoice(e.target.value)}
          >
            <option value="">Nomzod tanlang…</option>
            {matchOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {matchLabel(c)}
                {view.match.auto?.id === c.id ? " · avto" : ""}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            size="xs"
            onClick={() => void saveMatch(Number(selectedMatchId))}
            disabled={!selectedMatchId || selectedMatchId === currentIdStr || locked}
          >
            {action === "match" ? "Bog'lanmoqda…" : "Bog'lash"}
          </Button>
          {view.match.current && (
            <Button variant="ghost" size="xs" onClick={() => void unlinkMatch()} disabled={locked}>
              {action === "unlink" ? "Uzilmoqda…" : "Uzish"}
            </Button>
          )}
        </div>
        <div>
          <Button variant="secondary" size="xs" onClick={() => void runAutomap()} disabled={locked}>
            {action === "automap" ? "Xaritalanmoqda…" : "Avto xaritalash"}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="text-[12px] text-ink-muted">
          Bog'lanmagan o'yinchilar: <span className="text-ink">{unmapped.length}</span> /{" "}
          {view.players.length}
        </div>
        {view.match.current && unmapped.length > 0 && (
          <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto">
            {unmapped.map((p) => {
              const remoteTeamId = remoteTeamFor(p);
              return (
                <li key={p.id} className="flex items-center gap-2 text-[12px]">
                  <span className="w-16 shrink-0 truncate text-[11px] text-ink-faint">
                    {teamLabel(p.teamId)}
                  </span>
                  <span className="w-7 text-right tabular-nums text-ink-faint">
                    {p.number ?? "—"}
                  </span>
                  <span className="w-40 truncate">
                    {p.firstName} <span className="font-semibold">{p.lastName}</span>
                  </span>
                  <select
                    className={SELECT}
                    value={playerChoice[p.id] ?? ""}
                    onChange={(e) => setPlayerChoice((c) => ({ ...c, [p.id]: e.target.value }))}
                  >
                    <option value="">— tanlang —</option>
                    {lineupForPlayer(p, remoteTeamId).map((l) => {
                      const score = p.candidates.find((c) => c.remoteId === l.id)?.score;
                      return (
                        <option key={l.id} value={l.id}>
                          {remoteTeamId === null ? `${l.teamName} · ` : ""}#{l.jersey ?? "?"}{" "}
                          {l.name}
                          {score !== undefined ? ` (${score.toFixed(2)})` : ""}
                        </option>
                      );
                    })}
                  </select>
                </li>
              );
            })}
          </ul>
        )}
        {view.match.current && unmapped.length > 0 && (
          <div>
            <Button
              variant="success"
              size="xs"
              onClick={() => void savePlayers()}
              disabled={locked || Object.values(playerChoice).every((v) => !v)}
            >
              {action === "players" ? "Saqlanmoqda…" : "Saqlash"}
            </Button>
          </div>
        )}
      </div>
      {mismatches.length > 0 && (
        <div className="flex flex-col gap-1 rounded-md border border-warning/50 bg-warning/10 p-2 text-[12px]">
          <div className="font-semibold text-warning">
            ⚠ Futbolka raqami Hudl bilan farq qiladi: {mismatches.length}
          </div>
          <ul className="flex flex-col gap-0.5">
            {mismatches.map((m) => (
              <li key={m.id} className="flex items-center gap-2">
                <span className="w-16 shrink-0 truncate text-[11px] text-ink-faint">
                  {teamLabel(m.teamId)}
                </span>
                <span className="min-w-0 flex-1 truncate text-ink">{m.name}</span>
                <span className="shrink-0 tabular-nums text-ink-soft">
                  PFL #{m.pfl ?? "—"} · Hudl #{m.hudl}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-ink-faint">
            Efirda PFL raqami chiqadi. To'g'rilash kerak bo'lsa — PFL'da tuzatib, loyihani resync
            qiling.
          </p>
        </div>
      )}
      {lineupById.size === 0 && view.match.current && (
        <p className="text-[11px] text-ink-faint">dostats lineup hali e'lon qilinmagan (bo'sh).</p>
      )}
    </div>
  );
}
